
import { Component, Prop, Vue, Watch } from 'nuxt-property-decorator';
import {
  Hooper,
  Navigation as HooperNavigation,
  Pagination as HooperPagination,
  Slide as HooperSlide,
} from 'hooper';
import 'hooper/dist/hooper.css';
import PromoCarouselSlide, {
  CardOrientations,
  TRefDoc,
} from './PromoCarouselSlide.vue';
import {
  SanityDocumentSchema,
  ISanityDocuments,
} from '~/types/sanity-documents';
import { SanityObjectSchema, ISanityObjects } from '~/types/sanity-objects';
import SanityReference from '~/components/Sanity/SanityReference.vue';

type TSlide = ISanityObjects[SanityObjectSchema.PROMO_CAROUSEL_ITEM] & {
  _key: string;
};

@Component({
  components: {
    PromoCarouselSlide,
    Hooper,
    HooperNavigation,
    HooperPagination,
    HooperSlide,
    SanityReference,
  },
  inheritAttrs: false,
})
export default class PromoCarousel extends Vue {
  @Prop(String)
  readonly id!: string;
  @Prop(String)
  readonly slug!: string;
  @Prop(String)
  readonly filterSlug!: string;
  @Prop(Boolean)
  readonly isSlideshow!: boolean;
  @Prop({ type: Boolean, default: true }) autoPlayCarousel!: boolean;

  mobileBreakpoint = 775;
  docType = SanityDocumentSchema.PROMO_CAROUSEL;
  schemas = SanityDocumentSchema;
  orientations = CardOrientations;
  filteredSlides: TSlide[] = [];

  async fetch() {
    if (!this.slug) {
      return;
    }

    await this.$sanityLookup.fetchDocument({
      _type: this.docType,
      _id: this.id,
      slug: this.slug,
      fetchReferences: true,
    });
  }

  get docState() {
    return (
      this.$sanityLookup.getDocumentFromIdOrSlug<
        ISanityDocuments[SanityDocumentSchema.PROMO_CAROUSEL]
      >({
        _type: this.docType,
        _id: this.id,
        slug: this.slug,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get slugState() {
    return (
      this.$sanityLookup.getSlug({
        _type: this.docType,
        slug: this.slug,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get hooperSettings() {
    return {
      autoPlay: this.autoPlayCarousel,
      infiniteScroll: this.filteredSlides.length > 1 ? true : false,
      itemsToShow: 1,
      mouseDrag: this.filteredSlides.length > 1 ? true : false,
      playSpeed: 5000,
      touchDrag: this.filteredSlides.length > 1 ? true : false,
      transition: 350,
      wheelControl: false,
    };
  }

  @Watch('docState.data.slides', { immediate: true })
  handleSlidesChange(current: TSlide[] | undefined) {
    if (!current?.length) {
      return;
    }

    const filteredSlides: TSlide[] = [];
    current?.forEach((slide) => {
      if (!(slide.startsAt || slide.endsAt) && !this.filterSlug) {
        filteredSlides.push(slide);
        return;
      }

      if (slide.documentReference) {
        const doc = this.$sanityLookup.getDocumentFromReference<TRefDoc>({
          _ref: slide.documentReference?._ref,
        });
        if (doc?.data?.slug && this.filterSlug === doc.data.slug) {
          return;
        }
      }

      const now = new Date();
      if (slide.startsAt) {
        const startsAtDate = new Date(slide.startsAt);
        if (startsAtDate > now) {
          return;
        }
      }

      if (slide.endsAt) {
        const endsAtDate = new Date(slide.endsAt);
        if (endsAtDate < now) {
          return;
        }
      }
      filteredSlides.push(slide);
    });

    this.filteredSlides = filteredSlides;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.width < this.mobileBreakpoint;
  }

  get orientation() {
    return this.isMobile
      ? this.orientations.PORTRAIT
      : this.orientations.LANDSCAPE;
  }
}
